import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Tv, Plus, Edit2, Trash2, Eye, EyeOff, Calendar, MapPin, Video, 
  Save, X, Play, ExternalLink, Copy, Check, Loader2, AlertCircle,
  Upload, Music, Clock, Images, RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  { value: 'trade_show', label: 'Trade Show' },
  { value: 'visit', label: 'Factory/Office Visit' },
  { value: 'launch', label: 'Product Launch' },
  { value: 'conference', label: 'Conference' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'award', label: 'Award/Recognition' },
  { value: 'milestone', label: 'Company Milestone' },
  { value: 'other', label: 'Other Event' }
];

const MUSIC_TRACKS = [
  { value: null, label: 'No Music', description: 'Silent slideshow' },
  { value: 'ambient1', label: 'Ambient Dreams', description: 'Calm, atmospheric' },
  { value: 'ambient2', label: 'Soft Corporate', description: 'Professional, upbeat' },
  { value: 'ambient3', label: 'Gentle Piano', description: 'Elegant, minimal' }
];

// Video input component for YouTube IDs
function VideoInput({ videos, setVideos, heroVideoId, setHeroVideoId }) {
  const [newVideoId, setNewVideoId] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');

  const extractYouTubeId = (input) => {
    if (!input) return '';
    if (input.length === 11 && !input.includes('/')) return input;
    const match = input.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : input;
  };

  const addVideo = () => {
    const youtubeId = extractYouTubeId(newVideoId);
    if (!youtubeId) return;
    
    const newVideo = {
      youtube_id: youtubeId,
      title: newVideoTitle || `Video ${videos.length + 1}`
    };
    
    const updatedVideos = [...videos, newVideo];
    setVideos(updatedVideos);
    
    if (updatedVideos.length === 1) {
      setHeroVideoId(youtubeId);
    }
    
    setNewVideoId('');
    setNewVideoTitle('');
  };

  const removeVideo = (index) => {
    const video = videos[index];
    const updatedVideos = videos.filter((_, i) => i !== index);
    setVideos(updatedVideos);
    
    if (video.youtube_id === heroVideoId && updatedVideos.length > 0) {
      setHeroVideoId(updatedVideos[0].youtube_id);
    } else if (updatedVideos.length === 0) {
      setHeroVideoId(null);
    }
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-white mb-2">Videos (YouTube)</label>
      
      {videos.length > 0 && (
        <div className="space-y-2 mb-4">
          {videos.map((video, index) => (
            <div key={index} className="flex items-center gap-3 bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
              <img 
                src={`https://img.youtube.com/vi/${video.youtube_id}/default.jpg`}
                alt={video.title}
                className="w-20 h-14 object-cover rounded"
              />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{video.title}</p>
                <p className="text-zinc-500 text-xs">{video.youtube_id}</p>
              </div>
              <button
                type="button"
                onClick={() => setHeroVideoId(video.youtube_id)}
                className={`px-2 py-1 rounded text-xs ${
                  heroVideoId === video.youtube_id 
                    ? 'bg-[#C41E3A] text-white' 
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                {heroVideoId === video.youtube_id ? '★ Hero' : 'Set Hero'}
              </button>
              <button
                type="button"
                onClick={() => removeVideo(index)}
                className="p-1 text-zinc-500 hover:text-red-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="text"
          placeholder="YouTube URL or Video ID"
          value={newVideoId}
          onChange={(e) => setNewVideoId(e.target.value)}
          className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm"
        />
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Video Title"
            value={newVideoTitle}
            onChange={(e) => setNewVideoTitle(e.target.value)}
            className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm"
          />
          <button
            type="button"
            onClick={addVideo}
            disabled={!newVideoId}
            className="px-4 py-2 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Gallery image input component
function GalleryImageInput({ images, setImages, coverImage, setCoverImage, profileId }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = React.useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const remainingSlots = 15 - images.length;
    const filesToUpload = files.slice(0, remainingSlots);
    
    if (filesToUpload.length === 0) {
      alert('Maximum 15 images allowed per gallery');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    const newImages = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      const formData = new FormData();
      formData.append('image', file);
      
      try {
        const response = await axios.post(
          `${API_URL}/api/profiles/${profileId}/showcase/gallery-upload`,
          formData,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
        newImages.push(response.data.url);
        setUploadProgress(Math.round(((i + 1) / filesToUpload.length) * 100));
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    
    const updatedImages = [...images, ...newImages];
    setImages(updatedImages);
    
    if (!coverImage && updatedImages.length > 0) {
      setCoverImage(updatedImages[0]);
    }
    
    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    const img = images[index];
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    
    if (img === coverImage && updatedImages.length > 0) {
      setCoverImage(updatedImages[0]);
    } else if (updatedImages.length === 0) {
      setCoverImage(null);
    }
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-white mb-2">
        Gallery Images ({images.length}/15)
      </label>
      
      {images.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-4">
          {images.map((img, index) => (
            <div key={index} className="relative group aspect-square">
              <img 
                src={img} 
                alt={`Gallery ${index + 1}`}
                className={`w-full h-full object-cover rounded-lg ${
                  coverImage === img ? 'ring-2 ring-[#C41E3A]' : ''
                }`}
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setCoverImage(img)}
                  className={`p-1.5 rounded-full text-xs ${
                    coverImage === img 
                      ? 'bg-[#C41E3A] text-white' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  title="Set as cover"
                >
                  ★
                </button>
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full text-white"
                  title="Remove"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              {coverImage === img && (
                <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-[#C41E3A] text-white text-xs rounded">
                  Cover
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || images.length >= 15}
          className="w-full py-3 border-2 border-dashed border-zinc-700 hover:border-[#C41E3A]/50 rounded-lg text-zinc-400 hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Uploading... {uploadProgress}%
            </>
          ) : (
            <>
              <Upload className="w-5 h-5" />
              {images.length === 0 ? 'Upload Images (up to 15)' : 'Add More Images'}
            </>
          )}
        </button>
      </div>
      
      <p className="text-zinc-500 text-xs">
        Recommended: 1920×1080 (16:9) images. First image or starred image will be the cover.
      </p>
    </div>
  );
}

// Showcase Slot Component - for empty or filled slot
function ShowcaseSlot({ 
  type, // 'video' or 'gallery'
  event, // existing event or null
  profileId,
  profileSlug,
  onCreateNew,
  onEdit,
  onDelete,
  onToggleVisibility
}) {
  const [copiedLink, setCopiedLink] = useState(false);
  const isVideo = type === 'video';
  const Icon = isVideo ? Video : Images;
  const title = isVideo ? 'Video Showcase' : 'Photo Gallery';
  const description = isVideo ? 'YouTube videos from events' : 'Photo slideshow with music';

  const copyEventLink = async () => {
    if (!event) return;
    const link = `${window.location.origin}/p/${profileSlug}/showcase/${event.id}`;
    await navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // Empty slot
  if (!event) {
    return (
      <div className="bg-zinc-800/30 border-2 border-dashed border-zinc-700 rounded-xl p-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-zinc-800 flex items-center justify-center">
            <Icon className="w-8 h-8 text-zinc-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
          <p className="text-zinc-500 text-sm mb-4">{description}</p>
          <button
            onClick={() => onCreateNew(type)}
            className="px-6 py-2 bg-[#C41E3A] hover:bg-[#A01830] text-white rounded-lg font-medium flex items-center gap-2 mx-auto"
          >
            <Plus className="w-4 h-4" />
            Add {title}
          </button>
        </div>
      </div>
    );
  }

  // Filled slot
  const thumbnail = isVideo 
    ? (event.hero_video_id ? `https://img.youtube.com/vi/${event.hero_video_id}/hqdefault.jpg` : null)
    : (event.cover_image || event.gallery_images?.[0]);
  
  const itemCount = isVideo ? event.videos?.length || 0 : event.gallery_images?.length || 0;
  const isVisible = event.visibility === 'published';

  return (
    <div className={`bg-zinc-800/50 border rounded-xl overflow-hidden ${isVisible ? 'border-green-500/30' : 'border-zinc-700'}`}>
      {/* Thumbnail */}
      <div className="aspect-video relative bg-zinc-900">
        {thumbnail ? (
          <img src={thumbnail} alt={event.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-12 h-12 text-zinc-700" />
          </div>
        )}
        {/* Overlay badges */}
        <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 rounded-md text-xs text-white flex items-center gap-1">
          <Icon className="w-3 h-3" />
          {itemCount} {isVideo ? (itemCount === 1 ? 'video' : 'videos') : 'photos'}
        </div>
        {/* Visibility badge */}
        <div className={`absolute top-2 right-2 px-2 py-1 rounded-md text-xs flex items-center gap-1 ${
          isVisible ? 'bg-green-500/80 text-white' : 'bg-zinc-700 text-zinc-300'
        }`}>
          {isVisible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          {isVisible ? 'Visible' : 'Hidden'}
        </div>
        {/* Play overlay for video */}
        {isVideo && thumbnail && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
              <Play className="w-6 h-6 text-white fill-white ml-1" />
            </div>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2">
          <Calendar className="w-3 h-3" />
          <span>{new Date(event.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          {event.location && (
            <>
              <span>•</span>
              <MapPin className="w-3 h-3" />
              <span className="truncate">{event.location}</span>
            </>
          )}
        </div>
        <h4 className="font-semibold text-white mb-1">{event.title}</h4>
        {event.description && (
          <p className="text-zinc-400 text-sm line-clamp-2 mb-3">{event.description}</p>
        )}
        
        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-zinc-700">
          <button
            onClick={() => onToggleVisibility(event)}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${
              isVisible 
                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
          >
            {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {isVisible ? 'Visible' : 'Hidden'}
          </button>
          <button
            onClick={copyEventLink}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg"
            title="Copy link"
          >
            {copiedLink ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
          </button>
          <Link
            to={`/p/${profileSlug}/showcase/${event.id}`}
            target="_blank"
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg"
            title="Preview"
          >
            <ExternalLink className="w-4 h-4" />
          </Link>
          <button
            onClick={() => onEdit(event)}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(event)}
            className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
            title="Delete & Replace"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Event form modal
function EventFormModal({ format, event, profileId, profileSlug, onSave, onClose }) {
  const isEditing = !!event?.id;
  const isVideo = format === 'video';
  
  const [formData, setFormData] = useState({
    title: event?.title || '',
    event_type: event?.event_type || 'other',
    event_date: event?.event_date?.split('T')[0] || new Date().toISOString().split('T')[0],
    location: event?.location || '',
    description: event?.description || '',
    visibility: event?.visibility || 'published'
  });
  const [videos, setVideos] = useState(event?.videos || []);
  const [heroVideoId, setHeroVideoId] = useState(event?.hero_video_id || null);
  const [galleryImages, setGalleryImages] = useState(event?.gallery_images || []);
  const [coverImage, setCoverImage] = useState(event?.cover_image || null);
  const [gallerySettings, setGallerySettings] = useState(event?.gallery_settings || {
    duration: 4,
    music_track: null,
    transition: 'fade'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      setError('Title is required');
      return;
    }
    
    if (isVideo && videos.length === 0) {
      setError('Please add at least one video');
      return;
    }
    if (!isVideo && galleryImages.length === 0) {
      setError('Please upload at least one image');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const payload = {
        ...formData,
        event_format: format,
        videos: isVideo ? videos : [],
        hero_video_id: isVideo ? heroVideoId : null,
        gallery_images: !isVideo ? galleryImages : [],
        gallery_settings: !isVideo ? gallerySettings : null,
        cover_image: !isVideo ? coverImage : null
      };
      
      if (isEditing) {
        await axios.put(`${API_URL}/api/profiles/${profileId}/showcase/${event.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/profiles/${profileId}/showcase`, payload, { withCredentials: true });
      }
      
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save showcase');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isVideo ? <Video className="w-5 h-5 text-[#C41E3A]" /> : <Images className="w-5 h-5 text-[#C41E3A]" />}
            <h2 className="text-xl font-bold text-white">
              {isEditing ? 'Edit' : 'Create'} {isVideo ? 'Video Showcase' : 'Photo Gallery'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Event/Activity Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Automechanika Dubai 2026"
              className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500"
              required
            />
          </div>
          
          {/* Event Type & Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">Event Type</label>
              <select
                value={formData.event_type}
                onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                {EVENT_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">Event Date</label>
              <input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              />
            </div>
          </div>
          
          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Dubai World Trade Centre, UAE"
              className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500"
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the event or activity..."
              rows={3}
              className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 resize-none"
            />
          </div>
          
          {/* Video or Gallery Content */}
          {isVideo ? (
            <VideoInput 
              videos={videos} 
              setVideos={setVideos}
              heroVideoId={heroVideoId}
              setHeroVideoId={setHeroVideoId}
            />
          ) : (
            <>
              <GalleryImageInput 
                images={galleryImages}
                setImages={setGalleryImages}
                coverImage={coverImage}
                setCoverImage={setCoverImage}
                profileId={profileId}
              />
              
              {/* Gallery Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Slide Duration
                  </label>
                  <select
                    value={gallerySettings.duration || 4}
                    onChange={(e) => setGallerySettings({ ...gallerySettings, duration: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  >
                    <option value={3}>3 seconds</option>
                    <option value={4}>4 seconds</option>
                    <option value={5}>5 seconds</option>
                    <option value={6}>6 seconds</option>
                    <option value={8}>8 seconds</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    <Music className="w-4 h-4 inline mr-1" />
                    Background Music
                  </label>
                  <select
                    value={gallerySettings.music_track || ''}
                    onChange={(e) => setGallerySettings({ ...gallerySettings, music_track: e.target.value || null })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  >
                    {MUSIC_TRACKS.map(track => (
                      <option key={track.value || 'none'} value={track.value || ''}>
                        {track.label} {track.description ? `- ${track.description}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
          
          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Visibility on Profile</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, visibility: 'published' })}
                className={`p-3 rounded-lg border text-left transition-all ${
                  formData.visibility === 'published'
                    ? 'bg-green-500/20 border-green-500/50 text-white'
                    : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-4 h-4" />
                  <span className="font-medium">Visible</span>
                </div>
                <p className="text-xs opacity-70">Show on your public profile</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, visibility: 'hidden' })}
                className={`p-3 rounded-lg border text-left transition-all ${
                  formData.visibility === 'hidden'
                    ? 'bg-zinc-600/20 border-zinc-500/50 text-white'
                    : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <EyeOff className="w-4 h-4" />
                  <span className="font-medium">Hidden</span>
                </div>
                <p className="text-xs opacity-70">Only accessible via direct link</p>
              </button>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-zinc-800">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-3 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white rounded-lg font-medium flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isEditing ? 'Save Changes' : 'Create Showcase'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete confirmation modal
function DeleteConfirmModal({ event, onConfirm, onCancel, createAfterDelete }) {
  const [deleting, setDeleting] = useState(false);
  const isVideo = event?.event_format === 'video';

  const handleDelete = async () => {
    setDeleting(true);
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <Trash2 className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Delete & Replace?</h3>
          <p className="text-zinc-400 mb-6">
            This will delete "<span className="text-white">{event?.title}</span>" and let you create a new {isVideo ? 'video showcase' : 'photo gallery'}.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={deleting}
              className="flex-1 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg font-medium flex items-center justify-center gap-2"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Delete & Create New
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Showcase Manager component
export default function ShowcaseManager({ profileId, profileSlug, isAdmin = false }) {
  const [videoEvent, setVideoEvent] = useState(null);
  const [galleryEvent, setGalleryEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormat, setCreateFormat] = useState(null); // 'video' or 'gallery'
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteEvent, setDeleteEvent] = useState(null);
  const [createAfterDelete, setCreateAfterDelete] = useState(false);

  useEffect(() => {
    fetchShowcase();
  }, [profileId]);

  const fetchShowcase = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/profiles/${profileId}/showcase?include_hidden=true`, { withCredentials: true });
      const events = response.data.events || [];
      
      // Find video and gallery events (take the first of each type)
      const video = events.find(e => e.event_format === 'video' || !e.event_format);
      const gallery = events.find(e => e.event_format === 'gallery');
      
      setVideoEvent(video || null);
      setGalleryEvent(gallery || null);
    } catch (err) {
      console.error('Error fetching showcase:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = (format) => {
    setCreateFormat(format);
    setEditingEvent(null);
    setShowCreateModal(true);
  };

  const handleEdit = (event) => {
    setCreateFormat(event.event_format || 'video');
    setEditingEvent(event);
    setShowCreateModal(true);
  };

  const handleDeleteRequest = (event) => {
    setDeleteEvent(event);
    setCreateAfterDelete(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteEvent) return;
    
    const format = deleteEvent.event_format || 'video';
    
    try {
      await axios.delete(`${API_URL}/api/profiles/${profileId}/showcase/${deleteEvent.id}`, { withCredentials: true });
      
      // Refresh and open create modal
      await fetchShowcase();
      setDeleteEvent(null);
      
      if (createAfterDelete) {
        setCreateFormat(format);
        setEditingEvent(null);
        setShowCreateModal(true);
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete showcase');
    }
    
    setCreateAfterDelete(false);
  };

  const handleToggleVisibility = async (event) => {
    const newVisibility = event.visibility === 'published' ? 'hidden' : 'published';
    try {
      await axios.put(`${API_URL}/api/profiles/${profileId}/showcase/${event.id}`, {
        visibility: newVisibility
      }, { withCredentials: true });
      fetchShowcase();
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  };

  const handleSave = () => {
    setShowCreateModal(false);
    setEditingEvent(null);
    setCreateFormat(null);
    fetchShowcase();
  };

  const activeCount = [videoEvent, galleryEvent].filter(e => e && e.visibility === 'published').length;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="showcase-manager">
      {/* Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-zinc-800/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#C41E3A]/20 rounded-lg flex items-center justify-center">
            <Tv className="w-5 h-5 text-[#C41E3A]" />
          </div>
          <div>
            <h3 className="font-semibold text-white">BrandStage Showcase</h3>
            <p className="text-zinc-500 text-sm">
              {activeCount === 0 ? 'No active showcases' : `${activeCount} active showcase${activeCount > 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {expanded ? (
            <svg className="w-5 h-5 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>
      
      {/* Content */}
      {expanded && (
        <div className="border-t border-zinc-800 p-4">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#C41E3A] mx-auto" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Video Showcase Slot */}
              <ShowcaseSlot
                type="video"
                event={videoEvent}
                profileId={profileId}
                profileSlug={profileSlug}
                onCreateNew={handleCreateNew}
                onEdit={handleEdit}
                onDelete={handleDeleteRequest}
                onToggleVisibility={handleToggleVisibility}
              />
              
              {/* Photo Gallery Slot */}
              <ShowcaseSlot
                type="gallery"
                event={galleryEvent}
                profileId={profileId}
                profileSlug={profileSlug}
                onCreateNew={handleCreateNew}
                onEdit={handleEdit}
                onDelete={handleDeleteRequest}
                onToggleVisibility={handleToggleVisibility}
              />
            </div>
          )}
          
          <p className="text-zinc-600 text-xs text-center mt-4">
            Add up to 1 video showcase and 1 photo gallery. Toggle visibility to show/hide on your public profile.
          </p>
        </div>
      )}
      
      {/* Create/Edit Modal */}
      {showCreateModal && createFormat && (
        <EventFormModal
          format={createFormat}
          event={editingEvent}
          profileId={profileId}
          profileSlug={profileSlug}
          onSave={handleSave}
          onClose={() => { setShowCreateModal(false); setEditingEvent(null); setCreateFormat(null); }}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteEvent && (
        <DeleteConfirmModal
          event={deleteEvent}
          onConfirm={handleDeleteConfirm}
          onCancel={() => { setDeleteEvent(null); setCreateAfterDelete(false); }}
          createAfterDelete={createAfterDelete}
        />
      )}
    </div>
  );
}
